import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Brain,
  Heart,
  Zap,
  Users,
  HeartCrack,
  BookOpen,
  Mic,
  SendHorizontal,
} from "lucide-react";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface TopicData {
  slug: string;
  title: string;
  description: string;
  buttonLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  conversation: Message[];
}

const topics: TopicData[] = [
  {
    slug: "ansiedade",
    title: "Sentindo ansiedade?",
    description:
      "Você não precisa enfrentar isso sozinho(a). Estou aqui para te ajudar a encontrar calma, a qualquer momento.",
    buttonLabel: "Ansiedade",
    icon: Brain,
    conversation: [
      {
        role: "user",
        content: "Estou me sentindo muito ansiosa, meu coração está acelerado e não consigo parar de pensar no pior...",
      },
      {
        role: "assistant",
        content:
          "Ei, respira fundo. Você está segura aqui. Esses pensamentos acelerados podem ser muito intensos, mas vamos lidar com isso juntos. Que tal fazermos uma respiração guiada agora?",
      },
      { role: "user", content: "Sim, por favor. Estou tentando me acalmar mas parece que nada funciona." },
      {
        role: "assistant",
        content:
          "Vamos lá, estou aqui com você. Inspire lentamente contando até 4... segure por 2... e expire contando até 6. Faz comigo. Você está indo muito bem. 💙",
      },
    ],
  },
  {
    slug: "desabafar",
    title: "Precisa colocar pra fora?",
    description: "Às vezes a gente só precisa de alguém que ouça sem julgar. Pode falar, estou aqui.",
    buttonLabel: "Desabafar",
    icon: Heart,
    conversation: [
      {
        role: "user",
        content: "Hoje foi um dia horrível. Ninguém entende o que eu passo e eu só precisava falar com alguém...",
      },
      {
        role: "assistant",
        content:
          "Sinto muito que você está tendo um dia tão difícil. Estou aqui para ouvir você, sem pressa e sem julgamentos. O que aconteceu?",
      },
      {
        role: "user",
        content: "É tanta coisa acumulada... Trabalho, família, tudo junto. Às vezes me sinto invisível.",
      },
      {
        role: "assistant",
        content:
          "Seus sentimentos são válidos. Carregar tudo isso sozinho(a) é muito pesado. Obrigada por confiar em mim. Vamos conversar sobre o que está te afetando mais?",
      },
    ],
  },
  {
    slug: "estresse",
    title: "O peso do dia a dia?",
    description:
      "Quando tudo parece demais, dar um passo de cada vez faz diferença. Vamos encontrar esse equilíbrio juntos.",
    buttonLabel: "Estresse",
    icon: Zap,
    conversation: [
      {
        role: "user",
        content: "Estou completamente sobrecarregado. O trabalho não para de acumular e sinto que vou explodir.",
      },
      {
        role: "assistant",
        content:
          "Essa sensação de sobrecarga é exaustiva. Você está fazendo o melhor que pode, mesmo quando não parece. O que está pesando mais agora?",
      },
      { role: "user", content: "São os prazos. Tenho mil coisas para entregar e não sei por onde começar." },
      {
        role: "assistant",
        content:
          "Entendo. Quando tudo parece urgente, fica difícil pensar com clareza. Que tal a gente organizar isso juntos? Podemos priorizar uma coisa de cada vez. 🌟",
      },
    ],
  },
  {
    slug: "relacionamento",
    title: "Relacionamento te preocupando?",
    description: "Sentimentos confusos sobre quem a gente ama são normais. Vamos entender isso juntos.",
    buttonLabel: "Relacionamento",
    icon: Users,
    conversation: [
      {
        role: "user",
        content:
          "Meu relacionamento está muito complicado. A gente briga por qualquer coisa e eu não sei mais o que fazer.",
      },
      {
        role: "assistant",
        content:
          "Conflitos frequentes são desgastantes e deixam a gente confuso(a) sobre o que sentir. O que você acha que está por trás dessas brigas?",
      },
      { role: "user", content: "Acho que a gente não se entende mais. Parece que falamos línguas diferentes." },
      {
        role: "assistant",
        content:
          "Essa desconexão dói muito. Às vezes, entender primeiro o que você precisa pode ajudar a clarear as coisas. O que você mais sente falta nessa relação?",
      },
    ],
  },
  {
    slug: "termino",
    title: "Passando por um término?",
    description: "A dor de um fim é real e merece ser acolhida. Cada pessoa tem seu ritmo para se reconstruir.",
    buttonLabel: "Término",
    icon: HeartCrack,
    conversation: [
      {
        role: "user",
        content: "Terminei meu relacionamento essa semana e estou me sentindo vazio(a). Não sei como seguir em frente.",
      },
      {
        role: "assistant",
        content:
          "Sinto muito. Términos deixam um vazio imenso, e está tudo bem se sentir assim. Você não precisa ter todas as respostas agora. Como você está cuidando de si hoje?",
      },
      { role: "user", content: "Não estou conseguindo fazer nada. Fico só pensando no que deu errado." },
      {
        role: "assistant",
        content:
          "É natural revisitar memórias, faz parte do processo. Mas você não precisa passar por isso sozinho(a). Estou aqui para te acompanhar, no seu ritmo. 💜",
      },
    ],
  },
  {
    slug: "estudos",
    title: "Pressão nos estudos?",
    description:
      "Provas e prazos podem ser esmagadores. Vamos encontrar formas de estudar sem deixar sua saúde mental de lado.",
    buttonLabel: "Estudos",
    icon: BookOpen,
    conversation: [
      {
        role: "user",
        content:
          "Tenho uma prova super importante na próxima semana e não consigo me concentrar. A ansiedade está atrapalhando tudo.",
      },
      {
        role: "assistant",
        content:
          "Pressão de provas é muito real. Quando a mente está ansiosa, fica difícil absorver qualquer coisa. Você já conseguiu estudar algo ou está travado(a)?",
      },
      { role: "user", content: "Travado(a) total. Toda vez que sento pra estudar, minha mente dispara." },
      {
        role: "assistant",
        content:
          "Vamos dar um passo atrás então. Primeiro, cuidar da ansiedade, depois voltar pro estudo com mais foco. Que tal uma técnica rápida de grounding agora?",
      },
    ],
  },
];

const topicSlugs = topics.map((t) => t.slug);

export function LandingAdSection() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const urlTopic = searchParams.get("topic");
  const initialIndex = urlTopic ? topicSlugs.indexOf(urlTopic) : 0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex >= 0 ? initialIndex : 0);

  // Controle do carrossel mobile
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  // Controle do scroll do mockup (efeito Apple)
  const mockupMessagesRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const currentTopic = topics[currentIndex];

  useEffect(() => {
    const topic = searchParams.get("topic");
    if (topic && topicSlugs.includes(topic)) {
      setCurrentIndex(topicSlugs.indexOf(topic));

      // Track ad landing
      window.dataLayer?.push({
        event: "ad_landing",
        ad_topic: topic,
      });
    }
  }, [searchParams]);

  // Auto scroll para o carrossel mobile
  useEffect(() => {
    if (!carouselRef.current) return;

    let animationId: number;
    let lastTimestamp = 0;
    const scrollSpeed = 0.5;

    const animate = (timestamp: number) => {
      if (!carouselRef.current) return;

      if (lastTimestamp === 0) {
        lastTimestamp = timestamp;
      }

      const deltaTime = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      if (isAutoScrolling && deltaTime < 100) {
        const scrollAmount = scrollSpeed * (deltaTime / 16);
        carouselRef.current.scrollLeft += scrollAmount;

        const maxScroll = carouselRef.current.scrollWidth / 3;
        if (carouselRef.current.scrollLeft >= maxScroll) {
          carouselRef.current.scrollLeft = 0;
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isAutoScrolling]);

  // Efeito de scroll do mockup estilo Apple
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !mockupMessagesRef.current) return;

      const section = sectionRef.current;
      const messagesContainer = mockupMessagesRef.current;

      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;

      const progress = Math.max(0, Math.min(1, -rect.top / (sectionHeight - window.innerHeight)));

      const maxScroll = messagesContainer.scrollHeight - messagesContainer.clientHeight;
      messagesContainer.scrollTop = progress * maxScroll;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentIndex]);

  const goToTopic = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? topics.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === topics.length - 1 ? 0 : prev + 1));
  };

  const handleCTA = () => {
    navigate("/auth?tab=signup");
  };

  const TopicIcon = currentTopic.icon;

  return (
    <section ref={sectionRef} className="relative h-[300vh]">
      {/* Conteúdo fixo que fica preso enquanto scrolla */}
      <div className="sticky top-0 min-h-screen flex items-center py-12 md:py-16 lg:py-24 bg-gradient-to-b from-background to-muted/30 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 max-w-6xl relative w-full">
          {/* Main content grid - text left, mockup right on desktop */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center mb-8 md:mb-12">
            {/* Left side - Text content */}
            <div className="text-center md:text-left order-1 md:order-1">
              {/* Badge */}
              <div className="flex justify-center md:justify-start mb-4 md:mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary">
                  <TopicIcon className="w-4 h-4" />
                </span>
              </div>

              {/* Title */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4 leading-tight">
                {currentTopic.title}
              </h2>

              {/* Description */}
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 md:mb-8 leading-relaxed">
                {currentTopic.description}
              </p>

              {/* CTA - visible on desktop */}
              <div className="hidden md:block">
                <Button
                  onClick={handleCTA}
                  size="lg"
                  className="px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all bg-primary hover:bg-primary/90"
                >
                  Começar agora
                </Button>
                <p className="text-muted-foreground text-sm mt-3">7 dias grátis • Sem cartão de crédito</p>
              </div>
            </div>

            {/* Right side - Chat Mockup */}
            <div className="flex justify-center order-2 md:order-2">
              <div className="relative w-full max-w-[320px] md:max-w-[340px]">
                {/* Phone Frame */}
                <div className="bg-foreground/5 rounded-[2.5rem] p-2 shadow-2xl border border-border/50 backdrop-blur-sm">
                  {/* Phone Notch */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-5 bg-foreground/10 rounded-full z-10" />

                  {/* Screen Container */}
                  <div className="bg-background rounded-[2.2rem] overflow-hidden shadow-inner">
                    {/* Chat Header - matches real KopfAI interface */}
                    <div className="bg-background px-4 py-3 pt-8 flex items-center justify-between">
                      <div className="w-8 h-8 rounded-lg border border-border flex items-center justify-center">
                        <div className="w-3 h-3 grid grid-cols-2 gap-0.5">
                          <div className="w-1 h-1 bg-foreground rounded-sm"></div>
                          <div className="w-1 h-1 bg-foreground rounded-sm"></div>
                          <div className="w-1 h-1 bg-foreground rounded-sm"></div>
                          <div className="w-1 h-1 bg-foreground rounded-sm"></div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-sm bg-gradient-to-br from-yellow-200 to-orange-300 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-orange-900">K</span>
                        </div>
                        <span className="text-foreground font-semibold text-base">KopfAI</span>
                      </div>

                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 text-xs font-semibold">Oi</span>
                      </div>
                    </div>

                    {/* Chat Messages - using exact KopfAI colors */}
                    <div
                      ref={mockupMessagesRef}
                      className="p-4 space-y-4 min-h-[380px] max-h-[380px] overflow-hidden bg-background"
                    >
                      {currentTopic.conversation.map((message, idx) => (
                        <div key={idx} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`max-w-[82%] px-4 py-3 text-sm leading-relaxed ${
                              message.role === "user"
                                ? "bg-blue-50 text-gray-800 rounded-2xl rounded-tr-sm"
                                : "bg-gray-100 text-gray-800 rounded-2xl rounded-tl-sm"
                            }`}
                          >
                            {message.content}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Chat Input - matches real interface */}
                    <div className="px-4 pb-6 pt-2 bg-background">
                      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-full px-4 py-3 shadow-sm">
                        <button className="flex-shrink-0">
                          <Mic className="w-5 h-5 text-gray-400" />
                        </button>
                        <span className="text-gray-400 text-sm flex-1">Digite sua mensagem...</span>
                        <button className="flex-shrink-0 w-9 h-9 rounded-full bg-yellow-300 flex items-center justify-center hover:bg-yellow-400 transition-colors">
                          <SendHorizontal className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Topic Navigation Pills */}
          <div className="mb-6 md:mb-8">
            {/* Desktop: Pills estáticos sem cortes */}
            <div className="hidden md:flex flex-wrap items-center justify-center gap-2 px-4">
              {topics.map((topic, idx) => {
                const Icon = topic.icon;
                const isActive = idx === currentIndex;
                return (
                  <button
                    key={topic.slug}
                    onClick={() => goToTopic(idx)}
                    className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 shadow-sm ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md scale-105"
                        : "bg-background border border-border text-foreground hover:bg-muted hover:border-primary/30"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {topic.buttonLabel}
                  </button>
                );
              })}
            </div>

            {/* Mobile: Infinite carousel */}
            <div className="md:hidden relative overflow-hidden">
              <div
                ref={carouselRef}
                className="flex gap-2 py-2 overflow-x-auto"
                style={{
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                onMouseEnter={() => setIsAutoScrolling(false)}
                onMouseLeave={() => setIsAutoScrolling(true)}
                onTouchStart={() => setIsAutoScrolling(false)}
                onTouchEnd={() => setIsAutoScrolling(true)}
              >
                {/* Triplicamos os tópicos para criar o efeito infinito */}
                {[...topics, ...topics, ...topics].map((topic, idx) => {
                  const Icon = topic.icon;
                  const actualIndex = idx % topics.length;
                  const isActive = actualIndex === currentIndex;
                  return (
                    <button
                      key={`${topic.slug}-${idx}`}
                      onClick={() => {
                        goToTopic(actualIndex);
                        setIsAutoScrolling(false);
                        setTimeout(() => setIsAutoScrolling(true), 3000);
                      }}
                      className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 shadow-sm flex-shrink-0 ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-md scale-105"
                          : "bg-background border border-border text-foreground hover:bg-muted hover:border-primary/30"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {topic.buttonLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* CTA - visible on mobile only */}
          <div className="text-center md:hidden mt-6">
            <Button
              onClick={handleCTA}
              size="lg"
              className="w-full max-w-sm px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all bg-primary hover:bg-primary/90"
            >
              Começar agora
            </Button>
            <p className="text-muted-foreground text-sm mt-3">7 dias grátis • Sem cartão de crédito</p>
          </div>
        </div>
      </div>

      <style>{`
        div[style*="msOverflowStyle"]::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
